import { DOCUMENT, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit, PLATFORM_ID, Renderer2, ViewChild } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { IonContent, MenuController, ModalController, Platform } from '@ionic/angular';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { CookiesService } from './services/cookies.service';
import { GoogleTagManagerService } from './services/google-tag-manager.service';
import { InitialDataService } from './services/initialData.service';
import { MetadataService } from './services/metadata.service';
import { TenantConfService } from './services/tenant-conf.service';
import { CookiesConfiguratorModalComponent } from './shared/components/cookies-configurator-modal/cookies-configurator-modal.component';
import { environment } from 'src/environments/environment';
import { scrollEventsService } from './services/scroll-events.service';
import {RecaptchaService} from "@syntonize/merlin-core";

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  @ViewChild(IonContent, { static: true }) content: IonContent;

  warMode = environment.warMode && localStorage.getItem('[merlin] warMode') !== 'syntonize';

  data$: Observable<any>;
  iframe$: Observable<any>;
  enabledModules$: Observable<any>;
  language: string;

  publicName$: any;
  tenantLogo$: any;
  favIcon: HTMLLinkElement;
  landingUrl: string;
  iframeTitle: any;

  cookies: any;
  cookiesOpen = true;

  tenantHasCinema: any;
  hasOnlyRestoration: any;
  onlyLanding: any;
  blogLink: any;

  metadata: any;

  constructor(
    private router: Router,
    public scrollEventsService: scrollEventsService,
    private menu: MenuController,
    private platform: Platform,
    private modalController: ModalController,
    private http: HttpClient,
    public tenantService: TenantConfService,
    private initialDataService: InitialDataService,
    private gtmService: GoogleTagManagerService,
    private metadataService: MetadataService,
    public cookiesService: CookiesService,
    private title: Title,
    private meta: Meta,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: any,
    @Inject(PLATFORM_ID) private platformId: Object,
    private recaptchaService: RecaptchaService
  ) {
    this.router.events.forEach((item) => {
      if (item instanceof NavigationEnd) {
        const gtmTag = {
          event: 'page',
          pageName: item.url,
        };
        this.gtmService.pushTag(gtmTag);
      }
    });
    this.loadCookieScript();
    this.initializeApp();

    this.tenantHasCinema = this.tenantService.hasCinema();
    this.hasOnlyRestoration = this.tenantService.hasOnlyRestoration();
    this.onlyLanding = this.tenantService.getOnlyLanding();
    this.blogLink = `${this.tenantService.getBlogUrl()}/${this.tenantService.getLanguage().toLowerCase()}/`;
  
    this.metadata = this.tenantService.getMetadata();
  }

  async initializeApp() {
    this.cookiesOpen = this.cookiesService.cookiesOpen;

    await this.platform.ready();

    this.title.setTitle(this.tenantService.getMetadata().title);
    this.meta.updateTag({
      property: 'description',
      content: this.tenantService.getMetadata().description,
    });

    this.language = this.tenantService.getLanguage();
    this.menu.enable(true, 'sidebar');

    this.initialDataService.getInitialData(this.language);
    this.publicName$ = this.initialDataService.publicName$;
    this.tenantLogo$ = this.initialDataService.logo$;
    this.data$ = this.initialDataService.tenant$.pipe(
        map((tenant: any) => {
          return {
            socialMedia: tenant.socialNetworkAndContact,
            virtualTourUrl: tenant.contentSectionAbout?.virtualTourUrl,
          }
        }),
        shareReplay()
    );

    this.enabledModules$ = this.initialDataService.enabledModules$.pipe(
      map((modules:any) => modules?.reduce((acc, module) => [...acc, module.name], [])),
      shareReplay()
    );

    this.document.documentElement.classList.add(this.tenantService.getTenantFromUrl());
    this.document.documentElement.lang = this.language.toLowerCase();

    if (!isPlatformServer(this.platformId)) {
      this.favIcon = document.querySelector('#appIcon');
      this.favIcon.href = 'assets/icon/' + this.tenantService.getTenantName() + '_favicon.png';

      // && environment.production
      this.checkBrowser();
      this.initialDataService.seoTitle.subscribe((_) => {
        this.metadataService.setMetadata(
          this.initialDataService.seoTitle.getValue(),
          this.initialDataService.seoDescription.getValue(),
          ''
        );
      });

      this.initialDataService.favicon$.subscribe((favicon) => {
        this.favIcon.href = favicon;
      });

      this.initialDataService.cookies$.subscribe((cookies) => {
        this.cookies = cookies;
      });

      this.initialDataService.tenant$.subscribe(async (tenant) => {
        if (this.tenantService.getTenantFromUrl() === 'almada') {
          this.meta.addTag({ name: 'facebook-domain-verification', content: 'p4bdeps8kjz0e43lhlwczfqjdxcxv0' });
        }
        await this.recaptchaService.load(tenant.recaptchaSiteKey)
      });

    }
  }

  scrollHeader(event) {
    this.scrollEventsService.scrollHeader(event, this.content);
  }

  checkBrowser() {
    if (navigator.userAgent.indexOf('Mac') > 0) {
      this.document.body.classList.add('mac-os');
    }
    if (navigator.userAgent.indexOf('Safari') > 0) {
      this.document.body.classList.add('safari');
    }
    if (navigator.userAgent.indexOf('Chrome') > 0) {
      this.document.body.classList.add('chrome');
    }

    if (window.devicePixelRatio === 1.25) {
      this.document.body.style.zoom = `${(1 / window.devicePixelRatio) * 100}%`;
      this.document.body.classList.add('device-ratio');
    }
  }

  ngOnInit() {
    this.iframe$ = this.http.get(`/iframe-active`).pipe(
      tap((iframe: any) => {
        if (typeof iframe.data == 'undefined') {
          return;
        }
        this.iframeTitle = iframe['data'][this.language].title;
        this.landingUrl = this.language + '/landing/' + iframe.slug;
      })
    )
  }

  private loadCookieScript() {
    const scriptData = this.tenantService.getTenantCookieScript()

    if (scriptData) {
      const script = this.renderer.createElement('script');
      script.id = scriptData.id
      script.src = scriptData.src;
      script.setAttribute('data-cbid', scriptData.cbid);
      // script.setAttribute('data-blockingmode', scriptData.blockingmode);
      script.type = 'text/javascript';

      this.renderer.appendChild(this.document.head, script);
    }

  }


  closeCookies(status) {
    this.cookiesOpen = true;
    this.cookiesService.closeCookies(status);

    if (status === 'custom') {
      this.openCookies();
    }
  }

  async openCookies() {
    const modal = await this.modalController.create({
      component: CookiesConfiguratorModalComponent,
      cssClass: 'cookies-modal',
      backdropDismiss: false,
      componentProps: this.cookies,
    });

    modal.onDidDismiss().then((data: any) => {
      if (data.data.cookiesSaved === true) {
        this.cookiesOpen = true;
      }
    });
    return await modal.present();
  }
}
